import {
  Component,
  Inject,
  Input,
  PLATFORM_ID,
} from '@angular/core';
import {ElevationStats, EventItem, Lookups, Race, RaceRoute, Waypoint, WaypointType} from "@model";
import {environment} from "@environment";
import {RaceRouteService, WaypointService} from "@services";
import {TranslocoService} from "@ngneat/transloco";
import {AbstractComponent} from "@components";
import {getLanguageValue} from "@utility";
import {DOCUMENT} from "@angular/common";

@Component({
  selector: 'race-map-modal',
  templateUrl: './race-map-modal.component.html',
  styleUrls: ['./race-map-modal.component.scss']
})
export class RaceMapModalComponent extends AbstractComponent {
  @Input() eventItem: EventItem | undefined;
  @Input({required: true}) languages: Lookups[] = [];

  race?: Race;
  raceRoute?: RaceRoute;
  waypointList: Waypoint[] = [];

  gpxWaypoints: Waypoint[] = [];
  waypointsStats: Map<number, ElevationStats> = new Map<number, ElevationStats>();
  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  controlPointsList: Waypoint[] = [];
  gpx?: any;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: Object,
    public translocoService: TranslocoService,
    private raceRouteService: RaceRouteService,
    private waypointService: WaypointService,
  ) {
    super();
  }

  onRaceChange(event: Event): void {
    const selectedValue = (event.target as HTMLSelectElement).value;

    const selectedId = Number((event.target as HTMLSelectElement).value);

    const races = this.eventItem?.racesList as Race[] | undefined;

    this.race = races!.find(r => {
      if (r.id == null) return false;
      return r.id === selectedId;
    });

    if (this.race) {
      this.resetData();

      this.raceRouteService.fetchRaceRoute(this.race.id!).subscribe(raceRoute => {
        this.raceRoute = raceRoute;
        this.downloadGpx();
      });

      this.waypointService.findEventWaypoints(this.eventItem!.id!).subscribe(waypoints => {
        this.waypointList = waypoints;
        this.filterControlPoints();
      });

      this.lat = environment.headquartersLat;
      this.lng = environment.headquartersLng;
    }
  }

  resetData(): void {
    this.gpx = undefined;
    this.gpxWaypoints = [];
    this.waypointsStats.clear();
    this.waypointList = [];
    this.controlPointsList = [];
  }

  filterControlPoints(): void {
    this.controlPointsList = this.waypointList.filter(wp => wp.waypointTypeId === WaypointType.CONTROL_WAYPOINT);
  }

  showRoute(): void {
    if (this.raceRoute?.content) {
      import("../../components/leaflet-maps/gpx-layer").then(({GPX}) => {
        this.gpx = new GPX(this.raceRoute!.content, {}, () => {});
        this.generateRaceProfileData();
      });
    } else {
      this.gpx = undefined;
      this.gpxWaypoints = [];
      this.waypointsStats.clear();
    }
  }

  private generateRaceProfileData(): void {
    if (this.gpx) {
      this.gpxWaypoints = [];
      const statsIndexes: number[] = [];
      let pointInd = 0;

      for (let i = 0; i < this.gpx._coords.length; i++) {
        const lat = this.gpx._coords[i].lat.toFixed(4);
        const lng = this.gpx._coords[i].lng.toFixed(4);

        for (let j = pointInd; j < this.waypointList.length; j++) {
          const wp = this.waypointList[j];
          if (wp.latitude?.toFixed(4) === lat && wp.longitude?.toFixed(4) === lng) {
            this.gpxWaypoints.push(wp);
            statsIndexes.push(i);
            pointInd = j + 1;
            break;
          }
        }

        if (statsIndexes.length === this.waypointList.length) break;
      }

      this.gpxWaypoints.forEach((wp, index) => {
        const curr = statsIndexes[index];
        const prev = index > 0 ? statsIndexes[index - 1] : undefined;

        if (wp.id) {
          const elevationMap = this.gpx.getElevationPointsMap();
          this.waypointsStats.set(wp.id, {
            gainFromStart: elevationMap[curr]?.gain,
            lossFromStart: elevationMap[curr]?.loss,
            gainFromPrevious: prev !== undefined ? elevationMap[curr]?.gain - elevationMap[prev]?.gain : 0,
            lossFromPrevious: prev !== undefined ? elevationMap[curr]?.loss - elevationMap[prev]?.loss : 0
          });
        }
      });
    }
  }

  downloadGPXFile(): void {
    if (this.raceRoute?.content && this.race?.raceSettings?.enableGPXDownload) {
      const filename: string = `${(getLanguageValue(this.race?.name, this.race?.nameEng, this.race?.nameIta, this.race?.nameSlo, this.race?.nameSr, this.race?.nameBs, this.translocoService.getActiveLang()) ?? "").split(/\s+/).join("_")}_route.gpx`;
      const decodedData: string = atob(this.raceRoute.content.split(",")[1]);

      const blob: Blob = new Blob([decodedData], {type: 'application/gpx+xml'});
      const link: HTMLAnchorElement = this.document.createElement('a');

      link.href = window.URL.createObjectURL(blob);
      link.download = filename;

      this.document.body.appendChild(link);
      link.click();
      this.document.body.removeChild(link);
    }
  }

  private downloadGpx() {
    if (this.raceRoute && this.raceRoute.route && this.raceRoute.route !== "") {
      this.raceRouteService.downloadGpx(this.raceRoute.route, (base64data: string) => {
        this.raceRoute!.content = base64data;
        this.showRoute();
      });
    }
  }

  onRaceSelection(selectedRace?: Race): void {
    this.race = selectedRace;

    if (this.race) {
      this.resetData();

      this.raceRouteService.fetchRaceRoute(this.race.id!).subscribe(raceRoute => {
        this.raceRoute = raceRoute;
        this.downloadGpx();
      });

      this.waypointService.findEventWaypoints(this.eventItem!.id!).subscribe(waypoints => {
        this.waypointList = waypoints;
        this.filterControlPoints();
      });

      this.lat = environment.headquartersLat;
      this.lng = environment.headquartersLng;
    } else {
      this.resetData();
    }
  }

}
